import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Camera, CheckCircle2, XCircle } from "lucide-react";
import { Modal, In } from "@/pages/Projects";
import EvidenceGallery from "@/components/EvidenceGallery";

export default function EvidencesTab({ onChanged }) {
  const [evidences, setEvidences] = useState([]);
  const [filter, setFilter] = useState("pendiente");
  const [gallery, setGallery] = useState(null);
  const [validating, setValidating] = useState(null);
  const [valForm, setValForm] = useState({ quantity: "", unit_cost: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/stock-evidences", { params: filter ? { status: filter } : {} });
    setEvidences(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const startValidate = (ev) => {
    setValidating(ev);
    setValForm({ quantity: String(ev.quantity), unit_cost: "", notes: "" });
  };

  const act = async (action) => {
    setSaving(true);
    try {
      await api.post(`/stock-evidences/${validating.id}/validate`, {
        action,
        quantity: action === "aprobar" ? Number(valForm.quantity) : undefined,
        unit_cost: action === "aprobar" && validating.type === "entrada" && valForm.unit_cost !== "" ? Number(valForm.unit_cost) : undefined,
        notes: valForm.notes || null,
      });
      toast.success(action === "aprobar" ? "Evidencia validada: movimiento aplicado al inventario" : "Evidencia rechazada");
      setValidating(null); load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const typeChip = (t) => (
    <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ background: t === "entrada" ? "rgba(22,163,74,0.18)" : "rgba(244,114,182,0.18)", color: t === "entrada" ? "var(--green)" : "var(--magenta)" }}>{t}</span>
  );
  const statusChip = (s) => {
    const map = { pendiente: ["PENDIENTE", "var(--yellow, #eab308)"], validada: ["VALIDADA", "var(--green)"], rechazada: ["RECHAZADA", "var(--red)"] };
    const [label, color] = map[s] || [s, "var(--cyan)"];
    return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ border: `1px solid ${color}`, color }}>{label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="evidences-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// EVIDENCIAS DE FORMULARIO EXTERNO · requieren validación del responsable</span>
        <select data-testid="evidence-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="input-cyber px-3 py-1.5 text-sm">
          <option value="pendiente" style={{ background: "#0a0f1d" }}>Pendientes</option>
          <option value="validada" style={{ background: "#0a0f1d" }}>Validadas</option>
          <option value="rechazada" style={{ background: "#0a0f1d" }}>Rechazadas</option>
          <option value="" style={{ background: "#0a0f1d" }}>Todas</option>
        </select>
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[950px]" data-testid="evidences-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-4 py-3">Fecha</th><th className="label-mono px-4 py-3">Almacén</th>
            <th className="label-mono px-4 py-3">Tipo</th><th className="label-mono px-4 py-3">Producto</th>
            <th className="label-mono px-4 py-3 text-right">Cantidad</th><th className="label-mono px-4 py-3">Reportó</th>
            <th className="label-mono px-4 py-3">Estado</th><th className="label-mono px-4 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {evidences.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500">Sin evidencias {filter ? `(${filter})` : ""}.</td></tr>}
            {evidences.map((ev) => (
              <tr key={ev.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-4 py-3 font-mono text-xs">{fmtDate(ev.created_at)}</td>
                <td className="px-4 py-3 text-xs text-slate-300">{ev.warehouse_name}</td>
                <td className="px-4 py-3">{typeChip(ev.type)}</td>
                <td className="px-4 py-3"><span className="font-semibold text-slate-100">{ev.product_name}</span>{ev.notes && <div className="text-[11px] text-slate-500">{ev.notes}</div>}</td>
                <td className="px-4 py-3 text-right font-mono">{ev.quantity} {ev.product_unit}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{ev.submitted_by}{ev.validated_by_name && <div className="text-slate-500">Validó: {ev.validated_by_name}</div>}</td>
                <td className="px-4 py-3">{statusChip(ev.status)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {ev.photos?.length > 0 && (
                    <button data-testid={`evidence-photos-${ev.id}`} onClick={() => setGallery({ photos: ev.photos, title: `${ev.product_name} · ${ev.submitted_by}` })} className="p-1 mr-2 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><Camera className="w-4 h-4" /></button>
                  )}
                  {ev.status === "pendiente" && (
                    <button data-testid={`validate-evidence-${ev.id}`} onClick={() => startValidate(ev)} className="text-xs font-semibold hover:underline" style={{ color: "var(--green)" }}>VALIDAR</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {validating && (
        <Modal onClose={() => setValidating(null)} title="Validar evidencia">
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              {typeChip(validating.type)} <span className="font-semibold text-slate-100 ml-1">{validating.product_name}</span> · {validating.warehouse_name}<br />
              <span className="text-xs text-slate-400">Reportado por {validating.submitted_by} · cantidad declarada: {validating.quantity} {validating.product_unit}</span>
            </p>
            <In label={`Cantidad confirmada (${validating.product_unit})`} testId="val-qty" type="number" value={valForm.quantity} onChange={(v) => setValForm({ ...valForm, quantity: v })} />
            {validating.type === "entrada" && <In label="Costo unitario MXN (opcional)" testId="val-unit-cost" type="number" value={valForm.unit_cost} onChange={(v) => setValForm({ ...valForm, unit_cost: v })} required={false} />}
            <div>
              <label className="label-mono block mb-1.5">Notas de validación</label>
              <textarea value={valForm.notes} onChange={(e) => setValForm({ ...valForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            {validating.type === "salida" && <p className="text-xs text-slate-400">Al aprobar una salida se descuenta del stock y se genera el gasto de materiales en la obra del almacén.</p>}
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button disabled={saving} data-testid="reject-evidence-btn" onClick={() => act("rechazar")} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50" style={{ borderColor: "var(--red)", color: "var(--red)" }}><XCircle className="w-3.5 h-3.5" /> Rechazar</button>
              <button disabled={saving} data-testid="approve-evidence-btn" onClick={() => act("aprobar")} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50"><CheckCircle2 className="w-3.5 h-3.5" /> Aprobar</button>
            </div>
          </div>
        </Modal>
      )}

      {gallery && <EvidenceGallery photos={gallery.photos} title={gallery.title} onClose={() => setGallery(null)} />}
    </div>
  );
}
